import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { openPreview, resolveShopUrl } from '../../../util/previewHelper';
import { ADMIN_RUNTIME_COMMANDS } from '../../../app/runtime/adminRuntime';
import { useShellAppContext } from '../context/useShellAppContext';

function normalizeAddress(value) {
  if (typeof value !== 'string') {
    return value || null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

export function getPreviewShopAddress({ storefrontUrl, shopAddress, shellDomain } = {}) {
  return normalizeAddress(storefrontUrl)
    || normalizeAddress(shopAddress)
    || normalizeAddress(shellDomain)
    || null;
}

export function usePreviewOpener(options = {}) {
  const reduxShopAddress = useSelector((state) => state.settings?.shopAddress ?? null);
  const { domain: shellDomain, storefrontUrl, runtime } = useShellAppContext();
  const previewShopAddress = getPreviewShopAddress({
    storefrontUrl: storefrontUrl || options.storefrontUrl || options.pluginShopUrl || options.shopUrl || null,
    shopAddress: options.shopAddress || reduxShopAddress,
    shellDomain: options.shellDomain || shellDomain,
  });

  const openStorefrontPreview = useCallback(() => {
    if (!previewShopAddress) {
      return Promise.reject(new Error('No store address configured'));
    }

    const resolveAndOpen = async () => {
      let targetUrl = await resolveShopUrl(previewShopAddress);
      if (runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.PREVIEW_RESOLVE_URL)) {
        try {
          const response = await runtime.sendMessageWithResponse(
            ADMIN_RUNTIME_COMMANDS.PREVIEW_RESOLVE_URL,
            { url: targetUrl, timeout: 20000 },
          );
          targetUrl = response?.finalUrl || response?.url || targetUrl;
        } catch (error) {
          // Redirect detection is best-effort. Opening the configured storefront
          // remains useful when either product adapter cannot resolve redirects.
          // eslint-disable-next-line no-console
          console.warn('Failed to resolve storefront preview URL:', error);
        }
      }
      return openPreview(targetUrl);
    };

    return resolveAndOpen();
  }, [previewShopAddress, runtime]);

  return {
    previewShopAddress,
    canOpenPreview: !!previewShopAddress,
    openStorefrontPreview,
  };
}
